import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/app/AuthContext";
import WordmarkLogo from "@/components/wynn/WordmarkLogo";
import { ArrowRight, ArrowLeft } from "lucide-react";

function formatError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : "")).filter(Boolean).join(" ");
  if (detail?.msg) return detail.msg;
  return "Something went wrong. Please try again.";
}

export default function AuthScreen() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("register");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      if (mode === "register") await register(name, email, password);
      else await login(email, password);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputBase =
    "w-full bg-transparent text-[#F4F1EC] placeholder:text-[#F4F1EC]/35 border-0 border-b border-[#F4F1EC]/25 rounded-none px-0 h-12 text-base font-body outline-none transition-colors focus:border-[#FFB7C6]";

  return (
    <div className="min-h-screen bg-[#070608] text-[#F4F1EC] flex flex-col">
      <div
        aria-hidden
        className="absolute inset-0 opacity-60 pointer-events-none"
        style={{
          background:
            "radial-gradient(50% 45% at 75% 15%, rgba(255,150,200,0.25) 0%, rgba(7,6,8,0) 60%), radial-gradient(45% 40% at 15% 85%, rgba(120,200,230,0.22) 0%, rgba(7,6,8,0) 65%)",
        }}
      />
      <div className="relative px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-[#F4F1EC]/60 hover:text-[#FFB7C6] transition-colors">
          <ArrowLeft size={16} strokeWidth={1.6} />
          <span className="font-body text-[11px] uppercase tracking-[0.2em]">Back to site</span>
        </Link>
        <WordmarkLogo height={20} />
      </div>

      <div className="relative flex-1 flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
        >
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#F4F1EC]/55 font-body">
            {mode === "register" ? "Create your space" : "Welcome back"}
          </div>
          <h1 className="mt-4 font-display tracking-tight leading-[1.05] text-[clamp(2.2rem,6vw,3.2rem)] font-medium">
            {mode === "register" ? (
              <>Step <span className="italic font-light text-aurora">inside</span>.</>
            ) : (
              <>Return to <span className="italic font-light text-aurora">the quiet</span>.</>
            )}
          </h1>

          <form onSubmit={submit} className="mt-10 space-y-7" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <label className="block text-[10px] uppercase tracking-[0.28em] font-body text-[#F4F1EC]/50 mb-2">
                  First name
                </label>
                <input
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Mira"
                  className={inputBase}
                  autoComplete="given-name"
                />
              </div>
            )}
            <div>
              <label className="block text-[10px] uppercase tracking-[0.28em] font-body text-[#F4F1EC]/50 mb-2">
                Email
              </label>
              <input
                data-testid="auth-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@studio.com"
                className={inputBase}
                autoComplete="email"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.28em] font-body text-[#F4F1EC]/50 mb-2">
                Password
              </label>
              <input
                data-testid="auth-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                className={inputBase}
                autoComplete={mode === "register" ? "new-password" : "current-password"}
              />
            </div>

            {error && (
              <p data-testid="auth-error" className="font-body text-sm text-[#FFB7C6]">
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="auth-submit-button"
              disabled={loading}
              className="group w-full h-12 px-7 font-body text-[12px] uppercase tracking-[0.22em] inline-flex items-center justify-center gap-2 bg-[#F4F1EC] text-[#070608] hover:bg-white disabled:opacity-60 transition-colors"
            >
              {loading ? "…" : mode === "register" ? "Create my space" : "Sign in"}
              {!loading && <ArrowRight size={16} strokeWidth={1.6} className="transition-transform group-hover:translate-x-1" />}
            </button>
          </form>

          <button
            data-testid="auth-toggle-mode"
            onClick={() => {
              setMode(mode === "register" ? "login" : "register");
              setError("");
            }}
            className="mt-8 font-body text-sm text-[#F4F1EC]/55 hover:text-[#FFB7C6] transition-colors"
          >
            {mode === "register" ? "Already have a space? Sign in" : "New here? Create your space"}
          </button>
        </motion.div>
      </div>
    </div>
  );
}
